import type { LocationQuery } from 'vue-router'

export const useQueryPrefix = () => {
  /**
   * @method removePrefix
   * @description Removes prefix from query parameters
   * @param {LocationQuery} query - Query object to clean
   * @returns {Recordable} Cleaned query object without prefixes
   * @example
   * // Input: { 'prefix:key': 'value' }
   * // Output: { 'key': 'value' }
   */
  function removePrefix(query: LocationQuery): Recordable {
    return Object.entries(query).reduce((acc: Recordable, [key, value]) => {
      const sanitizedKey: string = key.includes(':') ? key.split(':')[1] : key
      acc[sanitizedKey] = value
      return acc
    }, {})
  }

  /**
   * @method addPrefix
   * @description Adds prefix to query parameters
   * @param {Recordable} query - Query object to prefix
   * @param {string} prefix - Prefix to add
   * @param {string[]} [filterKeys] - Optional keys to prefix (if not provided, all keys will be prefixed)
   * @returns {Recordable} Query object with prefixes
   * @example
   * // Input: { key: 'value' }, 'prefix'
   * // Output: { 'prefix:key': 'value' }
   */
  function addPrefix(
    query: LocationQuery,
    prefix: string,
    filterKeys?: string[],
  ): Recordable {
    return Object.entries(query).reduce((acc: Recordable, [key, value]) => {
      if (filterKeys?.includes(key)) {
        acc[`${prefix}:${key}`] = value
      } else {
        acc[key] = value
      }
      return acc
    }, {})
  }

  return {
    removePrefix,
    addPrefix,
  }
}
